/**
 * Anchored overlays: menus, portalled dropdowns, and pagination.
 *
 * The grid needs these three and nothing in the app had them yet. They are portalled
 * to the body because the grid's filter row lives inside a sticky header inside a
 * horizontally scrolling container — a dropdown rendered in place gets clipped by the
 * scroll container, which is the classic reason filter dropdowns look broken in
 * data grids.
 */
import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

/* ------------------------------------------------------------------ escape */

/**
 * Escape closes one thing: the topmost.
 *
 * Every layer that can be dismissed used to listen on the document independently, so a
 * single press reached all of them at once — Escape inside a picker inside a dialog
 * closed the picker *and* the dialog, throwing away everything typed. Registering here
 * means the innermost open thing handles the key and the rest do not see it.
 */
const escapeStack: Array<() => void> = []

export function useEscape(open: boolean, onEscape: () => void): void {
  const latest = useRef(onEscape)
  latest.current = onEscape

  useEffect(() => {
    if (!open) return
    const entry = () => latest.current()
    escapeStack.push(entry)

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Only the innermost. The others stay registered so closing this one hands the
      // key back to whatever was underneath.
      if (escapeStack[escapeStack.length - 1] !== entry) return
      event.stopPropagation()
      entry()
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      const at = escapeStack.indexOf(entry)
      if (at >= 0) escapeStack.splice(at, 1)
    }
  }, [open])
}

/* ------------------------------------------------------------------ Portal */

/**
 * Which portal a subtree was rendered from.
 *
 * Every one of these panels is portalled to the body to escape scroll containers, and
 * that breaks the usual way of asking "was this click inside me": a picker opened from
 * inside a menu is a DOM sibling of that menu, not a descendant. The menu saw a click
 * on one of its own dropdown's options as a click outside itself and shut — which is
 * what happened when picking a task in the header's timer, and would have happened
 * anywhere else the two were nested.
 *
 * So each portal records the portal it was opened from, and dismissal walks that chain
 * instead of the DOM tree.
 */
const OverlayScope = createContext<string | null>(null)

export function Portal({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false)
  const id = useId()
  const parent = useContext(OverlayScope)
  useEffect(() => setMounted(true), [])
  if (!mounted) return null
  return createPortal(
    <OverlayScope.Provider value={id}>
      {/* display:contents so the wrapper carries the link without becoming a box that
          could disturb the panel's own positioning. */}
      <div style={{ display: 'contents' }} data-overlay-id={id} data-overlay-parent={parent ?? undefined}>
        {children}
      </div>
    </OverlayScope.Provider>,
    document.body,
  )
}

/** True when the click landed in this panel, or in a panel opened from it. */
function withinScope(target: Node | null, ownerId: string | null): boolean {
  if (!ownerId) return false
  let node: Element | null =
    target instanceof Element ? target : ((target as ChildNode | null)?.parentElement ?? null)
  const seen = new Set<string>()
  while (node) {
    const scope = node.closest('[data-overlay-id]') as HTMLElement | null
    if (!scope) return false
    const id = scope.dataset.overlayId
    if (id === ownerId) return true
    if (!id || seen.has(id)) return false
    seen.add(id)
    const parentId = scope.dataset.overlayParent
    if (!parentId) return false
    node = document.querySelector(`[data-overlay-id="${CSS.escape(parentId)}"]`)
  }
  return false
}

type Align = 'start' | 'end'

function useAnchored(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  align: Align,
  width?: number,
) {
  const [style, setStyle] = useState<React.CSSProperties>({})
  const panelRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const place = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const panelHeight = panelRef.current?.offsetHeight ?? 240
      const panelWidth = width ?? panelRef.current?.offsetWidth ?? rect.width
      // Flip above when there is not room below, so a menu near the bottom of the
      // window is never half off-screen.
      const below = window.innerHeight - rect.bottom
      const flip = below < panelHeight + 12 && rect.top > below
      const left =
        align === 'end'
          ? Math.max(8, rect.right - panelWidth)
          : Math.min(rect.left, window.innerWidth - panelWidth - 8)
      setStyle({
        position: 'fixed',
        top: flip ? undefined : rect.bottom + 4,
        bottom: flip ? window.innerHeight - rect.top + 4 : undefined,
        left: Math.max(8, left),
        width,
        zIndex: 60,
        maxHeight: Math.max(160, (flip ? rect.top : below) - 16),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, anchorRef, align, width])

  return { style, panelRef }
}

function useDismiss(
  open: boolean,
  close: () => void,
  refs: React.RefObject<HTMLElement | null>[],
) {
  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (refs.some((r) => r.current?.contains(target))) return
      // A panel this one opened counts as inside it. Without this, choosing an option
      // in a nested picker dismisses the menu the picker was opened from.
      const owner = refs
        .map((r) => (r.current?.closest('[data-overlay-id]') as HTMLElement | null)?.dataset.overlayId)
        .find(Boolean)
      if (withinScope(target, owner ?? null)) return
      close()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, close, refs])

  // Escape is handled through the shared stack rather than here, so a dismissible thing
  // inside a dialog does not take the dialog with it.
  useEscape(open, close)
}

/* -------------------------------------------------------------------- Menu */

export function Menu({
  trigger,
  children,
  align = 'end',
  width = 240,
  label,
}: {
  trigger: (props: { ref: React.Ref<HTMLButtonElement>; onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode
  children: (close: () => void) => ReactNode
  align?: Align
  width?: number
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const { style, panelRef } = useAnchored(open, anchorRef, align, width)
  useDismiss(open, () => setOpen(false), [anchorRef, panelRef])

  return (
    <>
      {trigger({
        ref: anchorRef,
        onClick: () => setOpen((v) => !v),
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })}
      {open && (
        <Portal>
          <div
            ref={panelRef}
            role="menu"
            aria-label={label}
            style={style}
            className="card overflow-y-auto py-1 shadow-xl"
          >
            {children(() => setOpen(false))}
          </div>
        </Portal>
      )}
    </>
  )
}

export function MenuItem({
  children,
  onClick,
  danger = false,
  disabled = false,
  icon,
}: {
  children: ReactNode
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-ink)]',
        !disabled && 'hover:bg-[var(--color-surface-sunk)]',
      )}
    >
      {icon && <span className="shrink-0 text-[var(--color-ink-faint)]">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider text-[var(--color-ink-faint)] uppercase">
      {children}
    </div>
  )
}

export function MenuDivider() {
  return <div className="my-1 border-t border-[var(--color-rule)]" />
}

/* ------------------------------------------------------- Portalled combobox */

export type Choice = { value: string; label: string; hint?: string | null }

/**
 * A select that portals its list. Used in the filter row, where an in-place
 * dropdown would be clipped by the scrolling table.
 */
export function PortalSelect({
  options,
  value,
  onChange,
  placeholder = 'Any',
  clearable = true,
  searchable = true,
  size = 'sm',
  ariaLabel,
  panelWidth,
  disabled = false,
}: {
  options: Choice[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  clearable?: boolean
  searchable?: boolean
  size?: 'sm' | 'md'
  ariaLabel?: string
  /** Shown but not changeable — an approved time entry, for instance. */
  disabled?: boolean
  /** Open wider than the trigger, for options too long to read in the control. */
  panelWidth?: number
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const anchorRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { style, panelRef } = useAnchored(open, anchorRef, 'start', panelWidth)
  useDismiss(open, () => setOpen(false), [anchorRef, panelRef])

  // Forget the search when the panel closes. Committing a choice cleared it, but
  // dismissing did not — so reopening showed the last thing somebody typed, filtered
  // to nothing, and the picker looked broken.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const selected = options.find((o) => o.value === value) ?? null
  // The hint counts as searchable: on the predecessor picker it holds the row number
  // and the key, and typing either of those found nothing — which reads as "this task
  // cannot be picked" rather than "this box only searches titles".
  const filtered = query.trim()
    ? options.filter((o) => {
        const q = query.trim().toLowerCase()
        return o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q)
      })
    : options

  const commit = (choice: Choice | undefined) => {
    if (!choice) return
    onChange(choice.value)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={anchorRef} className="relative">
      <div
        className={clsx(
          'flex items-center gap-1 rounded-[var(--radius-control)] border border-[var(--color-rule-strong)] bg-[var(--color-surface)]',
          size === 'sm' ? 'h-7 px-1.5 text-[12px]' : 'h-8.5 px-2 text-[13px]',
          open && 'ring-2 ring-[var(--color-focus)]/35',
        )}
      >
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={clsx(
            'min-w-0 flex-1 truncate text-left',
            !selected && 'text-[var(--color-ink-faint)]',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          {selected?.label ?? placeholder}
        </button>
        {clearable && selected && (
          <button
            type="button"
            aria-label="Clear"
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
            className="shrink-0 px-0.5 text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
          >
            ×
          </button>
        )}
        <span aria-hidden className="shrink-0 text-[var(--color-ink-faint)]">
          ▾
        </span>
      </div>

      {open && (
        <Portal>
          <div
            ref={panelRef}
            style={{
              ...style,
              width: panelWidth ?? Math.max(180, anchorRef.current?.offsetWidth ?? 180),
            }}
            className="card overflow-hidden shadow-xl"
          >
            {searchable && options.length > 8 && (
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActive(0)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setActive((i) => Math.min(i + 1, filtered.length - 1))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActive((i) => Math.max(i - 1, 0))
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    commit(filtered[active])
                  }
                }}
                placeholder="Search…"
                className="w-full border-b border-[var(--color-rule)] bg-transparent px-3 py-1.5 text-[13px] outline-none"
              />
            )}
            <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-[12px] text-[var(--color-ink-faint)]">
                  No matches
                </li>
              )}
              {filtered.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(option)}
                    className={clsx(
                      'flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left text-[13px]',
                      index === active && 'bg-[var(--color-accent-soft)]',
                      option.value === value && 'font-medium',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {option.hint && (
                      <span className="shrink-0 text-[11px] text-[var(--color-ink-faint)]">
                        {option.hint}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Portal>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- MultiSelect */

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  ariaLabel,
}: {
  options: Choice[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const { style, panelRef } = useAnchored(open, anchorRef, 'start')
  useDismiss(open, () => setOpen(false), [anchorRef, panelRef])

  const toggle = (key: string) =>
    onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key])

  return (
    <div ref={anchorRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className="input flex min-h-8.5 flex-wrap items-center gap-1 text-left"
      >
        {value.length === 0 && (
          <span className="text-[var(--color-ink-faint)]">{placeholder}</span>
        )}
        {value.map((key) => (
          <span
            key={key}
            className="rounded-full bg-[var(--color-accent-soft)] px-2 py-0.5 text-[11px] text-[var(--color-accent-ink)]"
          >
            {options.find((o) => o.value === key)?.label ?? key}
          </span>
        ))}
      </button>
      {open && (
        <Portal>
          <div
            ref={panelRef}
            style={{ ...style, width: Math.max(200, anchorRef.current?.offsetWidth ?? 200) }}
            className="card max-h-64 overflow-y-auto py-1 shadow-xl"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value.includes(option.value)}
                onClick={() => toggle(option.value)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-[var(--color-surface-sunk)]"
              >
                <span
                  aria-hidden
                  className={clsx(
                    'grid size-3.5 shrink-0 place-items-center rounded-[3px] border text-[9px]',
                    value.includes(option.value)
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-contrast)]'
                      : 'border-[var(--color-rule-strong)]',
                  )}
                >
                  {value.includes(option.value) ? '✓' : ''}
                </span>
                <span className="truncate">{option.label}</span>
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  )
}

/* -------------------------------------------------------------- Pagination */

/** Page numbers with ellipses, first and last always reachable. */
export function pageWindow(page: number, count: number, siblings = 1): (number | '…')[] {
  if (count <= 5 + siblings * 2) {
    return Array.from({ length: count }, (_, i) => i + 1)
  }
  const left = Math.max(page - siblings, 1)
  const right = Math.min(page + siblings, count)
  const showLeftGap = left > 3
  const showRightGap = right < count - 2

  if (!showLeftGap && showRightGap) {
    const span = 3 + siblings * 2
    return [...Array.from({ length: span }, (_, i) => i + 1), '…', count]
  }
  if (showLeftGap && !showRightGap) {
    const span = 3 + siblings * 2
    return [1, '…', ...Array.from({ length: span }, (_, i) => count - span + 1 + i)]
  }
  return [
    1,
    '…',
    ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
    '…',
    count,
  ]
}

export function Pagination({
  page,
  count,
  onChange,
}: {
  page: number
  count: number
  onChange: (page: number) => void
}) {
  if (count <= 1) return null
  return (
    <nav aria-label="Pagination" className="flex items-center gap-0.5">
      <PageButton disabled={page === 1} onClick={() => onChange(page - 1)} label="Previous">
        ‹
      </PageButton>
      {pageWindow(page, count).map((entry, i) =>
        entry === '…' ? (
          <span key={`gap-${i}`} className="px-1 text-[12px] text-[var(--color-ink-faint)]">
            …
          </span>
        ) : (
          <PageButton
            key={entry}
            current={entry === page}
            onClick={() => onChange(entry)}
            label={`Page ${entry}`}
          >
            {entry}
          </PageButton>
        ),
      )}
      <PageButton disabled={page === count} onClick={() => onChange(page + 1)} label="Next">
        ›
      </PageButton>
    </nav>
  )
}

function PageButton({
  children,
  onClick,
  disabled,
  current,
  label,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  current?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      aria-current={current ? 'page' : undefined}
      onClick={onClick}
      className={clsx(
        'tnum grid h-7 min-w-7 place-items-center rounded-[var(--radius-control)] px-1.5 text-[12px]',
        'disabled:cursor-not-allowed disabled:opacity-40',
        current
          ? 'bg-[var(--color-accent)] font-medium text-[var(--color-accent-contrast)]'
          : 'text-[var(--color-ink-dim)] hover:bg-[var(--color-surface-sunk)]',
      )}
    >
      {children}
    </button>
  )
}
